import React from 'react';
import { AlertTriangle, RotateCcw, Home } from 'lucide-react';

/**
 * ErrorBoundary — cattura errori imprevisti di rendering React che altrimenti
 * farebbero comparire una schermata bianca senza alcun messaggio.
 *
 * Deve essere una classe: getDerivedStateFromError/componentDidCatch non hanno
 * equivalente negli hook (i Error Boundary sono l'unica eccezione in React).
 *
 * Uso consigliato: un'istanza "globale" attorno a tutta l'app (in index.js,
 * per coprire anche eventuali errori di montaggio molto in alto) e un'altra
 * più interna attorno alle rotte, così un crash in una singola pagina non fa
 * sparire anche l'header/i banner globali che la circondano.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log in console per il debug in sviluppo; se in futuro si aggiunge un
    // servizio di error tracking (Sentry, ecc.) è qui che va agganciato.
    console.error('ErrorBoundary ha catturato un errore:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
          <div className="max-w-md w-full text-center">
            <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-xl font-black text-slate-900 tracking-tight mb-2">
              Qualcosa è andato storto
            </h1>
            <p className="text-sm text-slate-500 mb-8">
              Si è verificato un errore imprevisto. Prova a ricaricare la pagina — se il problema persiste, contattaci.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReload}
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-2xl text-white text-sm font-bold transition-all"
                style={{ background: '#2563EB' }}
              >
                <RotateCcw className="w-4 h-4" /> Ricarica la pagina
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-2xl text-slate-600 text-sm font-bold border border-slate-200 bg-white hover:bg-slate-50 transition-all"
              >
                <Home className="w-4 h-4" /> Torna alla home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
